import {
  setupWebGpuMultipass,
  cleanupWebGpuMultipass,
  applyWebGpuMultipass
} from "./index-kkvzt8b7.mjs";
import {
  GAUSSIAN_BLUR_WEBGPU_SHADER
} from "./index-618ky75w.mjs";
import {
  assertOptionalFiniteNumber
} from "./index-wxhvgv2s.mjs";
import {
  assertEffectParamsObject
} from "./index-4fvp93ps.mjs";

// src/linear-progressive-blur/index.ts
import { Internals as Internals2 } from "remotion";

// src/linear-progressive-blur/linear-progressive-blur-runtime.ts
import { Internals } from "remotion";

// src/linear-progressive-blur/linear-progressive-blur-shaders.ts
var LINEAR_PROGRESSIVE_BLUR_VS = `#version 300 es
layout(location = 0) in vec2 aPos;
layout(location = 1) in vec2 aUv;
out vec2 vUv;
void main() {
	vUv = aUv;
	gl_Position = vec4(aPos, 0.0, 1.0);
}
`;
var buildLinearProgressiveBlurFs = (direction) => {
  const dirVec = direction === "horizontal" ? "vec2(1.0, 0.0)" : "vec2(0.0, 1.0)";
  return `#version 300 es
precision highp float;

in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uSource;
uniform vec2 uTexelSize;
uniform vec2 uStart;
uniform vec2 uEnd;
uniform float uStartBlur;
uniform float uEndBlur;

const int MIN_KERNEL_HALF = 4;
const int MAX_KERNEL_HALF = 32;
const float TARGET_SAMPLE_DISTANCE_PX = 2.0;
const vec2 DIRECTION = ${dirVec};

float progressiveRadius(vec2 uv) {
	vec2 gradient = uEnd - uStart;
	float gradientLengthSq = dot(gradient, gradient);
	if (gradientLengthSq <= 0.0000001) {
		return max(0.0, uStartBlur);
	}

	float progress = dot(uv - uStart, gradient) / gradientLengthSq;
	return max(0.0, mix(uStartBlur, uEndBlur, clamp(progress, 0.0, 1.0)));
}

void main() {
	// Public UV coordinates use top-left origin, matching canvas/CSS coordinates.
	float radius = progressiveRadius(vec2(vUv.x, 1.0 - vUv.y));
	if (radius <= 0.0) {
		fragColor = texture(uSource, vUv);
		return;
	}

	int kernelHalf = int(
		min(
			float(MAX_KERNEL_HALF),
			max(float(MIN_KERNEL_HALF), ceil(radius / TARGET_SAMPLE_DISTANCE_PX))
		)
	);
	float pixelStride = radius / float(kernelHalf);
	float sigma = max(radius / 3.0, 0.0001);
	float twoSigmaSq = 2.0 * sigma * sigma;

	vec4 sum = vec4(0.0);
	float weightSum = 0.0;

	for (int i = -MAX_KERNEL_HALF; i <= MAX_KERNEL_HALF; ++i) {
		if (abs(i) > kernelHalf) {
			continue;
		}

		float offsetPx = float(i) * pixelStride;
		float w = exp(-(offsetPx * offsetPx) / twoSigmaSq);
		vec2 uv = vUv + DIRECTION * uTexelSize * offsetPx;
		sum += texture(uSource, uv) * w;
		weightSum += w;
	}

	fragColor = sum / weightSum;
}
`;
};

// src/linear-progressive-blur/linear-progressive-blur-runtime.ts
var { createWebGL2ContextError } = Internals;
var compileShader = (gl, type, source) => {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error("Failed to create WebGL shader");
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Linear progressive blur shader compile failed: ${log ?? "(no log)"}`);
  }
  return shader;
};
var linkProgram = (gl, vs, fs) => {
  const program = gl.createProgram();
  if (!program) {
    throw new Error("Failed to create WebGL program");
  }
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Linear progressive blur program link failed: ${log ?? "(no log)"}`);
  }
  return program;
};
var createProgram = (gl, vertexSource, fragmentSource) => {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = linkProgram(gl, vs, fs);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return program;
};
var getUniforms = (gl, program) => ({
  uSource: gl.getUniformLocation(program, "uSource"),
  uTexelSize: gl.getUniformLocation(program, "uTexelSize"),
  uStart: gl.getUniformLocation(program, "uStart"),
  uEnd: gl.getUniformLocation(program, "uEnd"),
  uStartBlur: gl.getUniformLocation(program, "uStartBlur"),
  uEndBlur: gl.getUniformLocation(program, "uEndBlur")
});
var createRgbaTexture = (gl) => {
  const texture = gl.createTexture();
  if (!texture) {
    throw new Error("Failed to create WebGL texture");
  }
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return texture;
};
var setupLinearProgressiveBlur = (target) => {
  const gl = target.getContext("webgl2", {
    premultipliedAlpha: true,
    alpha: true,
    preserveDrawingBuffer: true
  });
  if (!gl) {
    throw createWebGL2ContextError("linear progressive blur effect");
  }
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  const programHorizontal = createProgram(gl, LINEAR_PROGRESSIVE_BLUR_VS, buildLinearProgressiveBlurFs("horizontal"));
  const programVertical = createProgram(gl, LINEAR_PROGRESSIVE_BLUR_VS, buildLinearProgressiveBlurFs("vertical"));
  const vao = gl.createVertexArray();
  if (!vao) {
    throw new Error("Failed to create WebGL vertex array");
  }
  gl.bindVertexArray(vao);
  const data = new Float32Array([
    -1,
    -1,
    0,
    0,
    1,
    -1,
    1,
    0,
    -1,
    1,
    0,
    1,
    1,
    1,
    1,
    1
  ]);
  const vbo = gl.createBuffer();
  if (!vbo) {
    throw new Error("Failed to create WebGL buffer");
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);
  gl.bindVertexArray(null);
  const textureSource = createRgbaTexture(gl);
  const textureIntermediate = createRgbaTexture(gl);
  const framebuffer = gl.createFramebuffer();
  if (!framebuffer) {
    throw new Error("Failed to create WebGL framebuffer");
  }
  const w = Math.max(1, target.width);
  const h = Math.max(1, target.height);
  gl.bindTexture(gl.TEXTURE_2D, textureIntermediate);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, textureIntermediate, 0);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    throw new Error(`Linear progressive blur framebuffer incomplete: 0x${status.toString(16)}`);
  }
  return {
    gl,
    programHorizontal,
    programVertical,
    vao,
    vbo,
    textureSource,
    textureIntermediate,
    framebuffer,
    horizontal: getUniforms(gl, programHorizontal),
    vertical: getUniforms(gl, programVertical)
  };
};
var cleanupLinearProgressiveBlur = (state) => {
  const {
    gl,
    programHorizontal,
    programVertical,
    vao,
    vbo,
    textureSource,
    textureIntermediate,
    framebuffer
  } = state;
  gl.deleteFramebuffer(framebuffer);
  gl.deleteTexture(textureSource);
  gl.deleteTexture(textureIntermediate);
  gl.deleteBuffer(vbo);
  gl.deleteProgram(programHorizontal);
  gl.deleteProgram(programVertical);
  gl.deleteVertexArray(vao);
};
var uploadSource = ({
  gl,
  textureSource,
  textureIntermediate,
  source,
  width,
  height,
  flipSourceY
}) => {
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, flipSourceY);
  gl.bindTexture(gl.TEXTURE_2D, textureSource);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  gl.bindTexture(gl.TEXTURE_2D, textureIntermediate);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.bindTexture(gl.TEXTURE_2D, null);
};
var setUniforms = ({
  gl,
  uniforms,
  width,
  height,
  params
}) => {
  if (uniforms.uSource)
    gl.uniform1i(uniforms.uSource, 0);
  if (uniforms.uTexelSize)
    gl.uniform2f(uniforms.uTexelSize, 1 / width, 1 / height);
  if (uniforms.uStart)
    gl.uniform2f(uniforms.uStart, params.start[0], params.start[1]);
  if (uniforms.uEnd)
    gl.uniform2f(uniforms.uEnd, params.end[0], params.end[1]);
  if (uniforms.uStartBlur)
    gl.uniform1f(uniforms.uStartBlur, params.startBlur);
  if (uniforms.uEndBlur)
    gl.uniform1f(uniforms.uEndBlur, params.endBlur);
};
var drawFullscreenQuad = (state) => {
  const { gl, vao } = state;
  gl.bindVertexArray(vao);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  gl.bindVertexArray(null);
};
var applyLinearProgressiveBlur = ({
  state,
  source,
  width,
  height,
  params,
  flipSourceY
}) => {
  const {
    gl,
    programHorizontal,
    programVertical,
    textureSource,
    textureIntermediate,
    framebuffer
  } = state;
  gl.viewport(0, 0, width, height);
  uploadSource({
    gl,
    textureSource,
    textureIntermediate,
    source,
    width,
    height,
    flipSourceY
  });
  gl.clearColor(0, 0, 0, 0);
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, textureSource);
  gl.useProgram(programHorizontal);
  setUniforms({
    gl,
    uniforms: state.horizontal,
    width,
    height,
    params
  });
  drawFullscreenQuad(state);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.bindTexture(gl.TEXTURE_2D, textureIntermediate);
  gl.useProgram(programVertical);
  setUniforms({
    gl,
    uniforms: state.vertical,
    width,
    height,
    params
  });
  drawFullscreenQuad(state);
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.useProgram(null);
};

// src/linear-progressive-blur/index.ts
var { createEffect } = Internals2;
var DEFAULT_START = [0, 0.5];
var DEFAULT_END = [1, 0.5];
var DEFAULT_START_BLUR = 0;
var DEFAULT_END_BLUR = 50;
var linearProgressiveBlurSchema = {
  start: {
    type: "uv-coordinate",
    min: -1,
    max: 2,
    step: 0.01,
    default: DEFAULT_START,
    description: "Start",
    visual: {
      type: "line",
      to: "end"
    }
  },
  end: {
    type: "uv-coordinate",
    min: -1,
    max: 2,
    step: 0.01,
    default: DEFAULT_END,
    description: "End"
  },
  startBlur: {
    type: "number",
    min: 0,
    max: 200,
    step: 1,
    default: DEFAULT_START_BLUR,
    description: "Start blur",
    hiddenFromList: false
  },
  endBlur: {
    type: "number",
    min: 0,
    max: 200,
    step: 1,
    default: DEFAULT_END_BLUR,
    description: "End blur",
    hiddenFromList: false
  }
};
var clampBlur = (value) => Math.max(0, value);
var resolveLinearProgressiveBlurParams = (params) => ({
  start: [
    ...params.start ?? DEFAULT_START
  ],
  end: [...params.end ?? DEFAULT_END],
  startBlur: clampBlur(params.startBlur ?? DEFAULT_START_BLUR),
  endBlur: clampBlur(params.endBlur ?? DEFAULT_END_BLUR)
});
var assertOptionalUvCoordinate = (value, name) => {
  if (value === undefined) {
    return;
  }
  if (!Array.isArray(value) || value.length !== 2 || value.some((item) => typeof item !== "number" || !Number.isFinite(item))) {
    throw new TypeError(`"${name}" must be a [number, number] tuple`);
  }
};
var validateLinearProgressiveBlurParams = (params) => {
  assertEffectParamsObject(params, "Linear progressive blur");
  assertOptionalUvCoordinate(params.start, "start");
  assertOptionalUvCoordinate(params.end, "end");
  assertOptionalFiniteNumber(params.startBlur, "startBlur");
  assertOptionalFiniteNumber(params.endBlur, "endBlur");
};
var linearProgressiveBlur = createEffect({
  type: "dev.remotion.effects.linearProgressiveBlur",
  label: "linearProgressiveBlur()",
  documentationLink: "https://www.remotion.dev/docs/effects/linear-progressive-blur",
  calculateKey: (params) => {
    const r = resolveLinearProgressiveBlurParams(params);
    return `linear-progressive-blur-${r.start.join(":")}-${r.end.join(":")}-${r.startBlur}-${r.endBlur}`;
  },
  implementations: {
    webgpu: Internals2.createEffectImplementation({
      setup: (target) => setupWebGpuMultipass(target, "linear-progressive-blur"),
      apply: ({ source, width, height, params, state, gpuDevice }) => {
        if (!gpuDevice)
          throw new Error("A WebGPU device is required for this effect");
        const r = resolveLinearProgressiveBlurParams(params);
        applyWebGpuMultipass({
          auxiliarySource: null,
          state,
          source,
          width,
          height,
          device: gpuDevice,
          passes: [
            {
              shader: GAUSSIAN_BLUR_WEBGPU_SHADER,
              uniforms: new Float32Array([
                width,
                height,
                1,
                0,
                0,
                1,
                r.startBlur,
                r.endBlur,
                ...r.start,
                ...r.end,
                0,
                0,
                0,
                0
              ]),
              source: -1,
              secondary: -1
            },
            {
              shader: GAUSSIAN_BLUR_WEBGPU_SHADER,
              uniforms: new Float32Array([
                width,
                height,
                0,
                1,
                0,
                1,
                r.startBlur,
                r.endBlur,
                ...r.start,
                ...r.end,
                0,
                0,
                0,
                0
              ]),
              source: 0,
              secondary: -1
            }
          ]
        });
      },
      cleanup: cleanupWebGpuMultipass
    }),
    webgl2: Internals2.createEffectImplementation({
      setup: (target) => setupLinearProgressiveBlur(target),
      apply: ({ source, width, height, params, state, flipSourceY }) => {
        const r = resolveLinearProgressiveBlurParams(params);
        applyLinearProgressiveBlur({
          state,
          source,
          width,
          height,
          params: r,
          flipSourceY
        });
      },
      cleanup: (state) => cleanupLinearProgressiveBlur(state)
    })
  },
  schema: linearProgressiveBlurSchema,
  validateParams: validateLinearProgressiveBlurParams
});
export {
  linearProgressiveBlur
};
